import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { Calendar, Download } from 'lucide-react';
import ReportHeader from '@/components/ReportHeader';
import { apiService } from '@/services/api';
import { useFilialSelecionada } from '@/hooks/useFilialSelecionada';

const Container = styled.div`
  padding: 16px;
`;

const Filtros = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px;
`;

const Campo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 12px;
  color: #6b7280;
`;

const Input = styled.input`
  height: 36px;
  padding: 0 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
`;

const Select = styled.select`
  height: 36px;
  padding: 0 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  min-width: 140px;
`;

const CheckboxRow = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
`;

const Acoes = styled.div`
  margin-left: auto;
  display: flex;
  gap: 8px;
`;

const Botao = styled.button`
  height: 36px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #d1d5db;
  background: #f9fafb;
  color: #111827;
  border-radius: 6px;
  cursor: pointer;
  &:hover { background: #f3f4f6; }
  &:disabled { opacity: .6; cursor: not-allowed; }
`;

const Tabela = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px;
  background: #f3f4f6;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
`;

const Td = styled.td<{ $nivelAlerta?: 'amarelo' | 'laranja' | 'vermelho' | 'normal' }>`
  padding: 10px;
  border-bottom: 1px solid #f1f5f9;
  ${({ $nivelAlerta }) => {
    if ($nivelAlerta === 'amarelo') return 'background: #f2e700;';
    if ($nivelAlerta === 'laranja') return 'background: #f29000;';
    if ($nivelAlerta === 'vermelho') return 'background: #f24000;';
    return '';
  }}
`;

const Tr = styled.tr<{ $nivelAlerta?: 'amarelo' | 'laranja' | 'vermelho' | 'normal' }>`
  ${({ $nivelAlerta }) => {
    if ($nivelAlerta === 'amarelo') return 'background: #f2e700;';
    if ($nivelAlerta === 'laranja') return 'background: #f29000;';
    if ($nivelAlerta === 'vermelho') return 'background: #f24000;';
    return '';
  }}
`;

const ScrollTop = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  height: 16px;
  margin: 0 16px 8px 16px;
`;

const ScrollBottom = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
`;

const Legenda = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 12px 16px;
  font-size: 12px;
  color: #6b7280;
  align-items: center;
`;

const LegendaItem = styled.span<{ $cor?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  ${({ $cor }) => $cor ? `background: ${$cor}; padding: 4px 10px; border-radius: 4px;` : ''}
`;

const STORAGE_MIN_ESTOQUE_KEY = 'relatorio-pneus-estoque-alerta-min-estoque';

const RelatorioPneusEstoqueAlerta: React.FC = () => {
  const { filialSelecionada } = useFilialSelecionada();
  const [diasInput, setDiasInput] = useState<string>('30');
  const [usarDiasPreset, setUsarDiasPreset] = useState<boolean>(true);
  const [minEstoqueInput, setMinEstoqueInput] = useState<string>(() => {
    if (typeof window === 'undefined') return '5';
    const valorSalvo = window.localStorage.getItem(STORAGE_MIN_ESTOQUE_KEY);
    const valor = parseInt(valorSalvo || '5', 10);
    return String(Number.isFinite(valor) && valor >= 1 ? valor : 5);
  });
  const [todasFiliais, setTodasFiliais] = useState<boolean>(true);
  const topScrollRef = useRef<HTMLDivElement | null>(null);
  const bottomScrollRef = useRef<HTMLDivElement | null>(null);
  const [contentWidth, setContentWidth] = useState<number>(0);

  const diasPresets = [30, 60, 90, 120, 180];
  const dias = useMemo(() => {
    if (usarDiasPreset) {
      const n = parseInt(diasInput, 10);
      return isNaN(n) ? 30 : Math.min(365, Math.max(1, n));
    }
    const n = parseInt(diasInput, 10);
    return isNaN(n) ? 30 : Math.min(365, Math.max(1, n));
  }, [diasInput, usarDiasPreset]);

  const minEstoque = useMemo(() => {
    const n = parseInt(minEstoqueInput, 10);
    return isNaN(n) ? 5 : Math.min(9999, Math.max(1, n));
  }, [minEstoqueInput]);

  const queryKey = useMemo(() => (
    ['relatorio-pneus-estoque-alerta', todasFiliais ? 'todas' : (filialSelecionada?.id || '-'), dias, minEstoque]
  ), [todasFiliais, filialSelecionada?.id, dias, minEstoque]);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey,
    enabled: todasFiliais || !!filialSelecionada?.id,
    queryFn: async () => {
      if (todasFiliais) return apiService.obterPneusEstoqueAlertaTodas({ dias, minEstoque });
      if (!filialSelecionada?.id) return null;
      return apiService.obterPneusEstoqueAlertaFilial(filialSelecionada.id, { dias, minEstoque });
    }
  });

  const linhas: any[] = useMemo(() => {
    const dados = data?.data?.dados || [];
    const ordem = { vermelho: 1, laranja: 2, amarelo: 3, normal: 4 };
    return [...dados].sort((a, b) => {
      const oa = ordem[a.NivelAlerta as keyof typeof ordem] ?? 5;
      const ob = ordem[b.NivelAlerta as keyof typeof ordem] ?? 5;
      if (oa !== ob) return oa - ob;
      const qa = Number(a.QtdAtual) || 0;
      const qb = Number(b.QtdAtual) || 0;
      if (qa !== qb) return qa - qb;
      const oma = Number(a.QtdOutrosMesmaMedidaFilial) || 0;
      const omb = Number(b.QtdOutrosMesmaMedidaFilial) || 0;
      if (oma !== omb) return oma - omb;
      return (Number(a.QtdOutrosMesmaMedidaOutrasFiliais) || 0) - (Number(b.QtdOutrosMesmaMedidaOutrasFiliais) || 0);
    });
  }, [data?.data?.dados]);

  const formatarDataBR = (valor: any): string => {
    if (!valor) return '';
    const s = String(valor);
    const match = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return `${match[3]}/${match[2]}/${match[1]}`;
    return s;
  };

  const colunasExibir = ['Filial', 'Produto', 'Medida', 'DataUltimaVenda', 'QtdAtual', 'QtdOutrosMesmaMedidaFilial', 'QtdOutrosMesmaMedidaOutrasFiliais', 'NivelAlerta'];
  const colunasLabel: Record<string, string> = {
    Filial: 'Filial',
    Produto: 'Produto',
    Medida: 'Medida',
    DataUltimaVenda: 'Última Venda',
    QtdAtual: 'Qtd. Atual',
    QtdOutrosMesmaMedidaFilial: 'Outros mesma medida',
    QtdOutrosMesmaMedidaOutrasFiliais: 'Outros mesma medida outras filiais',
    NivelAlerta: 'Alerta'
  };

  useEffect(() => {
    const bottom = bottomScrollRef.current;
    if (!bottom) return;
    const update = () => setContentWidth(bottom.scrollWidth);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [linhas]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_MIN_ESTOQUE_KEY, String(minEstoque));
  }, [minEstoque]);

  const handleTopScroll = () => {
    const top = topScrollRef.current;
    const bottom = bottomScrollRef.current;
    if (top && bottom && bottom.scrollLeft !== top.scrollLeft) {
      bottom.scrollLeft = top.scrollLeft;
    }
  };

  const handleBottomScroll = () => {
    const top = topScrollRef.current;
    const bottom = bottomScrollRef.current;
    if (top && bottom && top.scrollLeft !== bottom.scrollLeft) {
      top.scrollLeft = bottom.scrollLeft;
    }
  };

  const handleExportar = () => {
    if (linhas.length === 0) return;
    const sep = ';';
    const colunasCsv = colunasExibir.map(c => colunasLabel[c] || c);
    const toCsvValue = (coluna: string, valor: any): string => {
      const raw = coluna === 'DataUltimaVenda'
        ? formatarDataBR(valor)
        : String(valor ?? '');
      const needsQuote = raw.includes('"') || raw.includes(sep) || /[\n\r]/.test(raw);
      const escaped = raw.replace(/"/g, '""');
      return needsQuote ? `"${escaped}"` : escaped;
    };

    const header = colunasCsv.join(sep);
    const body = linhas.map((linha) =>
      colunasExibir.map((c) => toCsvValue(c, linha[c])).join(sep)
    ).join('\n');
    const contexto = todasFiliais ? 'todas' : (filialSelecionada?.nome?.replace(/[^\w\-\s]/g, '') || 'filial');
    const nomeArquivo = `pneus_estoque_alerta_${contexto}_ultimos_${dias}_dias.csv`;

    const csvContent = `\ufeff${header}\n${body}`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = nomeArquivo;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const nivelAlertaLabel: Record<string, string> = {
    normal: '-',
    amarelo: 'Atenção',
    laranja: 'Atenção alta',
    vermelho: 'Crítico'
  };

  return (
    <Container>
      <ReportHeader
        title="Pneus - Alerta de Estoque"
        subtitle={`${data?.data?.totalRegistros ?? 0} produtos com vendas nos últimos ${dias} dias`}
        rightActions={(
          <Acoes>
            <Botao onClick={handleExportar} disabled={isLoading || isFetching || linhas.length === 0}>
              <Download size={16} /> Exportar
            </Botao>
            <Botao onClick={() => refetch()} disabled={isLoading || isFetching}>
              <Calendar size={16} /> Aplicar
            </Botao>
          </Acoes>
        )}
      />

      <Filtros>
        <Campo>
          <Label>Período (últimos dias)</Label>
          {usarDiasPreset ? (
            <Select
              value={diasPresets.includes(parseInt(diasInput, 10)) ? diasInput : 'outro'}
              onChange={(e) => {
                const v = e.target.value;
                if (v === 'outro') {
                  setUsarDiasPreset(false);
                  setDiasInput(diasInput || '30');
                } else {
                  setDiasInput(v);
                }
              }}
            >
              {diasPresets.map((d) => (
                <option key={d} value={d}>{d} dias</option>
              ))}
              <option value="outro">Personalizado...</option>
            </Select>
          ) : (
            <Input
              type="number"
              min={1}
              max={365}
              placeholder="Ex: 45"
              value={diasInput}
              onChange={(e) => setDiasInput(e.target.value)}
            />
          )}
        </Campo>
        <Campo>
          <Label>&nbsp;</Label>
          <CheckboxRow>
            <input
              type="checkbox"
              checked={!usarDiasPreset}
              onChange={(e) => {
                const personalizado = e.target.checked;
                setUsarDiasPreset(!personalizado);
                if (!personalizado && !diasPresets.includes(parseInt(diasInput, 10))) setDiasInput('30');
              }}
            />
            Personalizado
          </CheckboxRow>
        </Campo>
        <Campo>
          <Label>&nbsp;</Label>
          <CheckboxRow>
            <input
              type="checkbox"
              checked={todasFiliais}
              onChange={(e) => setTodasFiliais(e.target.checked)}
            />
            Todas as Filiais
          </CheckboxRow>
        </Campo>
        <Campo>
          <Label>Estoque menor que:</Label>
          <Input
            type="number"
            min={1}
            max={9999}
            value={minEstoqueInput}
            onChange={(e) => setMinEstoqueInput(e.target.value)}
            onBlur={() => setMinEstoqueInput(String(minEstoque))}
          />
        </Campo>
      </Filtros>

      <Legenda>
        <strong>Legenda:</strong>
        <LegendaItem $cor="#f2e700">Amarelo</LegendaItem>
        <span>Estoque &lt; {minEstoque}, mas há outros com mesma medida na filial</span>
        <LegendaItem $cor="#f29000">Laranja</LegendaItem>
        <span>Estoque da medida na filial &lt; {minEstoque}</span>
        <LegendaItem $cor="#f24000">Vermelho</LegendaItem>
        <span>Crítico: sem alternativas na filial nem em outras filiais</span>
      </Legenda>

      <ScrollTop ref={topScrollRef} onScroll={handleTopScroll}>
        <div style={{ width: contentWidth }} />
      </ScrollTop>

      <ScrollBottom ref={bottomScrollRef} onScroll={handleBottomScroll}>
        <Tabela>
          <thead>
            <tr>
              {colunasExibir.map((c) => (
                <Th key={c}>{colunasLabel[c] || c}</Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><Td colSpan={colunasExibir.length}>Carregando...</Td></tr>
            ) : linhas.length === 0 ? (
              <tr><Td colSpan={colunasExibir.length}>Nenhum produto PNEUS com vendas no período</Td></tr>
            ) : (
              linhas.map((linha, idx) => (
                <Tr key={idx} $nivelAlerta={linha.NivelAlerta}>
                  {colunasExibir.map((c) => (
                    <Td key={c} $nivelAlerta={c === 'NivelAlerta' ? undefined : linha.NivelAlerta}>
                      {c === 'DataUltimaVenda'
                        ? formatarDataBR(linha[c])
                        : c === 'NivelAlerta'
                          ? nivelAlertaLabel[linha[c]] || linha[c]
                          : String(linha[c] ?? '')}
                    </Td>
                  ))}
                </Tr>
              ))
            )}
          </tbody>
        </Tabela>
      </ScrollBottom>
    </Container>
  );
};

export default RelatorioPneusEstoqueAlerta;
